import whatsappAdminService from "../../../services/admin/whatsappService";
import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { Button } from "@mui/material";
import { AxiosError } from "axios";
import ChannelConnectionsAdminService from "services/admin/channelConnections";
import { toast } from "react-toastify";

interface WhatsappConnectProps {
  channelConnection: any;
  refetch: () => void;
}

// Статус подключения канала
export const CONNECTION_STATUS = {
  PENDING: "PENDING",
  WAITING_QR: "WAITING_QR",
  CONNECTED: "CONNECTED",
  DISCONNECTED: "DISCONNECTED",
  ERROR: "ERROR",
};

// Статус канала
export const CHANNEL_STATUS = {
  PENDING: "PENDING",
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
  ARCHIVED: "ARCHIVED",
};

/**
 * Как это выглядит для менеджера в админке
1. Менеджер заходит в раздел "WhatsApp".
2. Нажимает "Подключить" → видит QR-код, сканирует.
3. После этого его номер работает в системе.
4. В админке он видит свои чаты и может отправлять сообщения клиентам.
 * @returns 
 */
export default function WhatsappConnect({ channelConnection, refetch }: WhatsappConnectProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Статус подключения Whatsapp
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    console.log("channelConnection: ", channelConnection?.connectionStatus);

    if (channelConnection.connectionStatus === CONNECTION_STATUS.WAITING_QR) {
      console.log("Рисуем QR код: ", channelConnection.qr);
      (QRCode as any).toCanvas(
        canvasRef.current,
        channelConnection.qr,
        { small: true },
        (error: any) => {
          if (error) {
            console.error("Вышла ошибка при получении QR кода", error?.message);
            setError(error?.message);
            return;
          }
        }
      );
    }
  }, [channelConnection]);

  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, 1 * 1000);

    return () => clearInterval(interval);
  }, []);

  // Подключаем Whatsapp: Создаем сессию и получаем QR код
  const handleConnect = async () => {
    try {
      await whatsappAdminService.connect(channelConnection.id);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDisconnect = async () => {
    if (window.confirm("Вы уверены, что хотите отсоединить Whatsapp?")) {
      await ChannelConnectionsAdminService.disconnectDevice(channelConnection.id);
      toast.success("Whatsapp отсоединен успешно!");
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    }
  };

  const sendTestMessage = async () => {
    try {
      const phone = window.prompt("Введите номер телефона клиента в формате 77771234567");
      if (phone) {
        await whatsappAdminService.sendMessage(
          channelConnection.id,
          phone,
          "Тестовое сообщение отправлено успешно!"
        );
        toast.success("Тестовое сообщение отправлено успешно!");
      }
    } catch (error: any) {
      console.error(error);
      toast.error("Ошибка при отправке тестового сообщения! " + error?.message);
    }
  };

  function renderBody() {
    if (channelConnection.connectionStatus === CONNECTION_STATUS.PENDING) {
      return (
        <div>
          <div>Whatsapp не подключен. Пожалуйста, подключите Whatsapp.</div>
          <Button onClick={handleConnect}>Подключить Whatsapp</Button>
        </div>
      );
    }
    if (channelConnection.connectionStatus === CONNECTION_STATUS.DISCONNECTED) {
      return (
        <div>
          <div>Whatsapp отсоединен. Чтобы использовать Whatsapp, подключите его заново.</div>
          <Button onClick={handleConnect}>Подключить Whatsapp</Button>
        </div>
      );
    }

    if (channelConnection.connectionStatus === CONNECTION_STATUS.WAITING_QR) {
      return (
        <div>
          <h3>Пожалуйста, отсканируйте QR код для подключения Whatsapp.</h3>

          <div className="mt-4 text-sm text-gray-500">
            QR код будет обновляться каждые 30 секунд для безопасности.
          </div>
        </div>
      );
    }

    if (channelConnection.connectionStatus === CONNECTION_STATUS.CONNECTED) {
      return (
        <div>
          <div className="mb-2">
            External Account ID: <strong>{channelConnection.externalAccountID}</strong>
          </div>
          <div className="mb-2">
            External Account Username: <strong>{channelConnection.externalAccountUserName}</strong>
          </div>
          <div className="mb-2">
            External Account First Name:{" "}
            <strong>{channelConnection.externalAccountFirstName}</strong>
          </div>
          <div className="flex gap-2 mt-8">
            <Button variant="outlined" color="primary" onClick={sendTestMessage}>
              Отправить тестовое сообщение
            </Button>
            <Button variant="outlined" color="error" onClick={handleDisconnect}>
              Отсоединить Whatsapp
            </Button>
          </div>
        </div>
      );
    }

    return null;
  }

  return (
    <section>
      {renderBody()}
      {channelConnection.connectionStatus === CONNECTION_STATUS.WAITING_QR && (
        <canvas id="canvas" ref={canvasRef}></canvas>
      )}
      {/* <pre>
        <div>Channel Connection: {JSON.stringify(channelConnection, null, 2)}</div>
      </pre> */}
    </section>
  );
}
